import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  Announcements,
  DndContext,
  closestCenter,
  MouseSensor,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  PointerActivationConstraint,
  DragStartEvent,
  DragOverlay,
  DragMoveEvent,
  DragEndEvent,
  DragOverEvent,
  MeasuringStrategy,
  DropAnimation,
  Modifier,
  defaultDropAnimation,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  createSnapModifier,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  buildTree,
  flattenTree,
  getProjection,
  getChildCount,
  removeItem,
  removeChildrenOf,
  setProperty,
} from './utilities';
import type {FlattenedItem, SensorContext, TreeItems} from './types';
// import {sortableTreeKeyboardCoordinates} from './keyboardCoordinates';
import {SortableTreeItem} from './components/TreeItem/SortableTreeItem';
import {CSS} from '@dnd-kit/utilities';


const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const dropAnimationConfig: DropAnimation = {
  keyframes({transform}) {
    return [
      {opacity: 1, transform: CSS.Transform.toString(transform.initial)},
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({active}) {
    active.node.animate([{opacity: 0}, {opacity: 1}], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

interface Props {
  activationConstraint?: PointerActivationConstraint;
  collapsible?: boolean;
  defaultItems: TreeItems;
  indentationWidth?: number;
  indicator?: boolean;
  removable?: boolean;
  category: string;
  activePageId: number | null;
  onMenuSort:(arg0: Array<object>) => void;
  onItemClick:(arg0: string, arg1: string | null, arg2: number, arg3: MouseEvent) => void;
  // handleItemClick:(arg0: string, arg1: string | null, arg2: number, arg3: MouseEvent) => void;
  btn1?: string;
  btn2?: string; 
  onBtn1Click?:(arg0: number) => void;
  onBtn2Click?:(arg0: number) => void;
  isDragActive?: boolean;
  allowNesting?: boolean;
  onItemContextMenu:(arg0: MouseEvent, arg1: string | number |  null) => void;
}

export function SortableTree({
  activationConstraint,
  collapsible,
  defaultItems,
  category,
  indicator = false,
  indentationWidth = 25,
  removable,
  activePageId,
  onMenuSort,
  onItemClick,
  btn1,
  btn2,
  onBtn1Click,
  onBtn2Click,
  isDragActive,
  allowNesting,
  onItemContextMenu
}: Props) {
  const [items, setItems] = useState(() => defaultItems);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [updateList, setUpdateList] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);



  useEffect(() => {
    if (updateList) {
      // console.log('items', items, defaultItems)
      onMenuSort(items)
      setUpdateList(false)
    }
  }, [updateList])

  useEffect(() => {
    if (defaultItems) {
   
      setItems(defaultItems)
    }
  }, [ defaultItems ])

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint,
  });
  // const touchSensor = useSensor(TouchSensor, {
  //   activationConstraint,
  // });

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);
    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, {children, collapsed, id}) =>
        collapsed && children.length ? [...acc, id] : acc,
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, items]);

  const projected =
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          indentationWidth
        )
      : null;
  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });
  // const [coordinateGetter] = useState(() =>
  //   sortableTreeKeyboardCoordinates(sensorContext, indicator, indentationWidth)
  // );
  // const sensors = useSensors(
  //   useSensor(PointerSensor),
  //   useSensor(KeyboardSensor, {
  //     coordinateGetter,
  //   })
  // );

  const sortedIds = useMemo(() => flattenedItems.map(({id}) => id), [
    flattenedItems,
  ]);
  const activeItem = activeId
    ? flattenedItems.find(({id}) => id === activeId)
    : null;

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const announcements: Announcements = {
    onDragStart({active}) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({active, over}) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({active, over}) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({active, over}) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({active}) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };

  const sensors = useSensors(mouseSensor);

  // function getText(id, depth) {
  //   let a = id;
  //   if (items && items.length > 0) {
  //     let item = items.find(a => a.id === id);
   
  //     a = item?.text
  //   }
  //   return a

  // }

  function handleBtn1(id: string) {
    console.log("handleBtn1", id)
    onBtn1Click && onBtn1Click(Number(id))
  }

  function handleBtn2(id: string) {
    console.log("handleBtn2", +id)
    onBtn2Click && onBtn2Click(Number(id))
  }

  function handleItemClick(id: string, parentId: string | null, childrenLength: number, event: MouseEvent) {
    // console.log('handleItemClick', id, parentId, childrenLength, e)\

    onItemClick && onItemClick(id, parentId, childrenLength, event)
  }

  function onContextMenu(e: MouseEvent, objId: string | number | null){
    onItemContextMenu(e, objId)
    // console.log('onContextMenu', e, objId)
  }


  // Compute dynamic modifiers list based on active item's allowNesting property
  const modifiersList = useMemo(() => {
    const mods = [restrictToWindowEdges];
    // Find the active item in the flattened tree
    const active = flattenedItems.find(({ id }) => id === activeId);
    // If the active item does NOT support nesting, restrict to vertical axis

    console.log('flattenedItems: active', active, activeId, flattenedItems, allowNesting)
    if (active && !allowNesting) {
        mods.push(restrictToVerticalAxis);
      }
    
    return mods;
  }, [flattenedItems, activeId]);

  return (
    <DndContext
      sensors={sensors}
      accessibility={{announcements}}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={modifiersList}
      // modifiers={[category === 'layers' ? (restrictToVerticalAxis) : (restrictToWindowEdges)]}
    >
      <SortableContext 
       items={sortedIds} 
       strategy={verticalListSortingStrategy}
       >
        {flattenedItems.map(({id, text, children, visible, locked, collapsed, depth, parentId, allowNesting}) => (
          <SortableTreeItem
            key={id + '-' + visible}
            id={id}
            value={text}
            category={category}
            depth={id === activeId && projected ? projected.depth : depth}
            indentationWidth={indentationWidth}
            indicator={indicator}
            collapsed={Boolean(collapsed && children.length)}
            activePageId={activePageId}
            btn1={btn1}
            btn2={btn2}
            onBtn1={() => handleBtn1(id)}
            onBtn2={() => handleBtn2(id)}
            onItemBtnClick={(event) => handleItemClick(id, parentId, children.length, event)}
            onCollapse={
              collapsible && children.length
                ? () => handleCollapse(id)
                : undefined
            }
            onContextMenuBtn={(event) => onContextMenu(event, id)}
            visible={visible}
            locked={locked}
            isDragActive={isDragActive}
            allowNesting={allowNesting}
            onRemove={removable ? () => handleRemove(id) : undefined}
          />
        ))}
        {createPortal(
          <DragOverlay
            dropAnimation={dropAnimationConfig}
            modifiers={indicator ? [adjustTranslate] : undefined}
          >
            {activeId && activeItem ? (
              <SortableTreeItem
                id={activeId}
                depth={activeItem.depth}
                category={category}
                activePageId={activePageId}
                clone
                childCount={getChildCount(items, activeId) + 1}
                value={activeItem.text}
                onContextMenuBtn={(event) => onContextMenu(event, activePageId)}
                indentationWidth={indentationWidth}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </SortableContext>
    </DndContext> 
    
  );

  function handleDragStart({active: {id: activeId}}: DragStartEvent) {
    setActiveId(activeId);
    setOverId(activeId);

    const activeItem = flattenedItems.find(({id}) => id === activeId);

    if (activeItem && (activeItem['children'].length === 0)) {
      // console.log('activeItem', activeItem)
      let parentId = null
      if (activeItem.parentId) {
        parentId = activeItem.parentId
      }
      setCurrentPosition({
        parentId: parentId,
        overId: activeId,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({delta}: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({over}: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  function handleDragEnd({active, over}: DragEndEvent) {
    resetState();

    if (projected && over) {
      const {depth, parentId} = projected;
      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({id}) => id === over.id);
      const activeIndex = clonedItems.findIndex(({id}) => id === active.id);
      const activeTreeItem = clonedItems[activeIndex];

      clonedItems[activeIndex] = {...activeTreeItem, depth, parentId};

      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);
      const newItems = buildTree(sortedItems);

      setItems(newItems);
      setUpdateList(true);
    }
  }

  function handleDragCancel() {
    resetState();
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty('cursor', '');
  }

  function handleRemove(id: UniqueIdentifier) {
    setItems((items) => removeItem(items, id));
  }

  function handleCollapse(id: UniqueIdentifier) {
    console.log('handleCollapse', id, items)
    setItems((items) =>
      setProperty(items, id, 'collapsed', (value) => {
       
        return !value;
      })
    );
  }

  function getMovementAnnouncement(
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier
  ) {
    if (overId && projected) {
      if (eventName !== 'onDragEnd') {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        } else {
          setCurrentPosition({
            parentId: projected.parentId,
            overId,
          });
        }
      }

      const clonedItems: FlattenedItem[] = JSON.parse(
        JSON.stringify(flattenTree(items))
      );
      const overIndex = clonedItems.findIndex(({id}) => id === overId);
      const activeIndex = clonedItems.findIndex(({id}) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement;
      const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
      const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`;
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
        } else {
          let previousSibling: FlattenedItem | undefined = previousItem;
          while (previousSibling && projected.depth < previousSibling.depth) {
            const parentId: UniqueIdentifier | null = previousSibling.parentId;
            previousSibling = sortedItems.find(({id}) => id === parentId);
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
          }
        }
      }
      // console.log(announcement)
      return announcement;
    }

    return;
  }
}

const adjustTranslate: Modifier = ({transform}) => {
  return {
    ...transform,
    y: transform.y - 25,
  };
};
